import React, { useEffect, useState } from 'react';
import { Quote, Copy, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Compact horizontal banner — for embedding inside dashboards
export const DailyTraderQuoteSlim = () => {
  const [quote, setQuote] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/quote/daily?_t=${Date.now()}`, { cache: 'no-store' });
        if (!r.ok) throw new Error();
        const data = await r.json();
        if (!cancelled) setQuote(data);
      } catch {
        if (!cancelled) setQuote(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleCopy = async () => {
    if (!quote?.share?.plain) return;
    try {
      await navigator.clipboard.writeText(quote.share.plain);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  if (!quote) return null;

  return (
    <div className="rounded-xl border border-golden/20 bg-gradient-to-r from-amber-950/30 via-black to-amber-950/20 p-4 my-4" data-testid="daily-quote-slim">
      <div className="flex items-start gap-3">
        <Quote className="w-5 h-5 text-golden/40 flex-shrink-0 mt-1" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-golden/60 mb-1">
            Daily Trader Wisdom · {quote.theme || 'Today'}
          </div>
          <p className="text-white text-sm md:text-base italic leading-relaxed line-clamp-3">
            "{quote.quote}"
          </p>
          <div className="mt-2 flex items-center flex-wrap gap-x-3 gap-y-1">
            <span className="text-golden text-xs font-semibold">— {quote.author}</span>
            {quote.source_book && <span className="text-gray-500 text-[11px]">{quote.source_book}{quote.year ? ` · ${quote.year}` : ''}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleCopy}
            className="p-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white"
            title="Copy quote with source"
            data-testid="quote-slim-copy"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
          <a
            href={quote.share?.x_share_url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-2 py-1.5 rounded-md bg-black border border-white/20 hover:bg-gray-900 text-white text-[10px] font-semibold"
            title="Share on X"
            data-testid="quote-slim-x"
          >
            X
          </a>
          <a
            href={quote.share?.telegram_share_url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-2 py-1.5 rounded-md bg-blue-500 hover:bg-blue-600 text-white text-[10px] font-semibold"
            title="Share on Telegram"
            data-testid="quote-slim-tg"
          >
            TG
          </a>
          <a
            href={quote.share?.whatsapp_share_url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-2 py-1.5 rounded-md bg-green-600 hover:bg-green-700 text-white text-[10px] font-semibold"
            title="Share on WhatsApp"
            data-testid="quote-slim-wa"
          >
            WA
          </a>
        </div>
      </div>
    </div>
  );
};

const DailyTraderQuote = () => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/quote/daily?_t=${Date.now()}`, {
          cache: 'no-store',
        });
        if (!r.ok) throw new Error('fetch failed');
        const data = await r.json();
        if (!cancelled) setQuote(data);
      } catch (e) {
        if (!cancelled) setQuote(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleCopy = async () => {
    if (!quote?.share?.plain) return;
    try {
      await navigator.clipboard.writeText(quote.share.plain);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* silently ignore — share buttons still work */
    }
  };

  if (loading) {
    return (
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-gradient-to-br from-amber-950/40 to-black border border-golden/20 rounded-2xl p-8 text-center">
            <div className="animate-pulse h-4 w-24 bg-golden/20 rounded mx-auto mb-4" />
            <div className="animate-pulse h-6 w-3/4 bg-gray-700 rounded mx-auto" />
          </div>
        </div>
      </section>
    );
  }

  if (!quote) return null;

  return (
    <section className="py-12 md:py-16 px-4" data-testid="daily-trader-quote">
      <div className="container mx-auto max-w-4xl">
        <div className="text-center mb-6">
          <span className="inline-block bg-golden/10 text-golden border border-golden/30 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest">
            Daily Trader Wisdom
          </span>
        </div>

        <div className="relative bg-gradient-to-br from-black via-amber-950/30 to-black border border-golden/30 rounded-2xl p-6 md:p-10 shadow-2xl overflow-hidden">
          {/* Decorative quote mark */}
          <Quote className="absolute top-4 left-4 w-12 h-12 text-golden/15" strokeWidth={1.5} />
          <Quote className="absolute bottom-4 right-4 w-12 h-12 text-golden/15 rotate-180" strokeWidth={1.5} />

          {quote.theme && (
            <div className="text-center mb-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-golden/70">
                {quote.theme}
              </span>
            </div>
          )}

          <blockquote className="text-center text-white text-lg md:text-2xl lg:text-3xl font-light leading-relaxed italic px-4 md:px-12 mb-6">
            “{quote.quote}”
          </blockquote>

          <div className="text-center mb-6">
            <div className="text-golden font-semibold text-base md:text-lg">— {quote.author}</div>
            {quote.source_book && (
              <div className="text-gray-400 text-sm mt-1">
                {quote.source_book}
                {quote.year && <span> · {quote.year}</span>}
              </div>
            )}
          </div>

          {/* Share buttons */}
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm rounded-lg transition-colors"
              data-testid="quote-copy-btn"
            >
              {copied ? <><Check className="w-4 h-4 text-green-400" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy</>}
            </button>

            <a
              href={quote.share?.x_share_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-black hover:bg-gray-900 border border-white/20 text-white text-sm rounded-lg transition-colors"
              data-testid="quote-share-x"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              Share on X
            </a>

            <a
              href={quote.share?.telegram_share_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors"
              data-testid="quote-share-telegram"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
              </svg>
              Telegram
            </a>

            <a
              href={quote.share?.whatsapp_share_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
              data-testid="quote-share-whatsapp"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24z"/>
              </svg>
              WhatsApp
            </a>
          </div>

          <p className="text-center text-xs text-gray-500 mt-5">
            New quote refreshes at midnight IST · {quote.date}
          </p>
        </div>
      </div>
    </section>
  );
};

export default DailyTraderQuote;
